import { T, useTranslate } from '@tolgee/react';
import { Dict } from 'mixpanel-browser';
import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { OrganizationType } from '../../../hooks/useAccessBadge';
import { useMetrics } from '../../../hooks/useMetrics';
import BackendService, {
  OrganizationAutocomplete,
} from '../../../services/backendService/backendService';
import GooglePlacesService, {
  GooglePlaceAutocomplete,
  GooglePlaceDetails,
} from '../../../services/googlePlacesService/googlePlacesService';
import { Input } from '../../_atoms/_Input';
import Select from '../../_atoms/_Select/Select';
import { TypeAheadInput } from '../../_atoms/TypeAheadInput';
import { TypeAheadInputRef } from '../../_atoms/TypeAheadInput/TypeAheadInput';

export type SelectedLocationType = {
  place: GooglePlaceDetails | null;
  placeAutocomplete: GooglePlaceAutocomplete;
};

export type SelectedOrganizationType = {
  organization: {
    id: string;
    name: string;
    type: OrganizationType;
    city?: string;
    region?: string;
    country?: string;
    custom: boolean;
  };
  organizationAutocomplete?: OrganizationAutocomplete;
};

export interface OrganizationSelectorProps {
  organizationType: OrganizationType | null;
  onSelectOrganizationType: (orgType: OrganizationType) => void;
  selectedLocation: SelectedLocationType | null;
  onSelectLocation: (location: SelectedLocationType | null) => void;
  selectedOrganization: SelectedOrganizationType | null;
  onSelectOrganization: (selectedOrganization: SelectedOrganizationType | null) => void;
  customOrganizationName: string | undefined;
  onSetCustomOrganizationName: (customOrganizationName: string | undefined) => void;
  needsCustomOrganizationName: boolean;
  onSetNeedsCustomOrganizationName: (customOrganizationName: boolean) => void;
  locationFetchError: string | undefined;
  onLocationFetchError: (locationFetchError: string | undefined) => void;
  hideOrganizationTypePicker?: boolean;
  className?: string;
  style?: React.CSSProperties;
  fluid?: boolean;
  metricEventNamePrefix?: string;
  // This is temporary and can be removed when we remove the districtSignupEnabled toggle
  disableSearchRetry: boolean;
}

export default function OrganizationSelector({
  className,
  organizationType,
  onSelectOrganizationType,
  selectedLocation,
  onSelectLocation,
  selectedOrganization,
  onSelectOrganization,
  customOrganizationName,
  onSetCustomOrganizationName,
  needsCustomOrganizationName,
  onSetNeedsCustomOrganizationName,
  locationFetchError,
  onLocationFetchError,
  hideOrganizationTypePicker,
  style,
  fluid = true,
  metricEventNamePrefix,
  // This is temporary and can be removed when we remove the districtSignupEnabled toggle
  disableSearchRetry,
}: OrganizationSelectorProps) {
  const metrics = useMetrics(true);
  const [hasSearched, setHasSearched] = useState(false);
  const logEvent = (event: string, eventProperties?: Dict) => {
    const componentEventProps = {
      selectedLocation,
      organizationType,
      selectedOrganization,
    };
    const eventName = [
      ...(metricEventNamePrefix ? [metricEventNamePrefix] : []),
      'OrganizationSelector',
      event,
    ].join('.');
    metrics.logEvent(eventName, {
      ...componentEventProps,
      ...eventProperties,
    });
  };

  // API for location typeahead
  const locationTARef = useRef<TypeAheadInputRef<GooglePlaceAutocomplete>>(null);

  // API for org name typeahead
  const orgTARef = useRef<TypeAheadInputRef<OrganizationAutocomplete>>(null);

  const { t } = useTranslate();
  // Organization type options
  const orgTypeOptions = useMemo(
    () => [
      { displayName: t('School', 'School'), value: OrganizationType.School },
      { displayName: t('District', 'District'), value: OrganizationType.District },
    ],
    [],
  );

  // City search functionality
  const fetchCities = async (
    query: string,
    limit?: number,
  ): Promise<GooglePlaceAutocomplete[]> => {
    const cities: GooglePlaceAutocomplete[] = await GooglePlacesService.autocomplete(
      query,
      limit,
    );
    return cities;
  };

  const fetchDetails = async (placeId: string): Promise<GooglePlaceDetails | null> => {
    return await GooglePlacesService.getDetails(placeId);
  };

  // Organization search functionality
  const fetchOrganizations = async (
    query: string,
    limit?: number,
  ): Promise<OrganizationAutocomplete[]> => {
    let result: OrganizationAutocomplete[] = [];
    if (!selectedLocation?.place) {
      return result;
    }

    const { country, zipCode, city, region } = selectedLocation.place;
    if (country !== 'US' || !zipCode || !city || !organizationType || !region) {
      return result;
    }

    try {
      const organizations: OrganizationAutocomplete[] = await BackendService.instance.organizations.autocomplete(
        {
          query,
          type: organizationType,
          country,
          region,
          city,
          zipCode,
          limit,
        },
      );
      if (!organizations) {
        logEvent('FetchOrganizations.NoResults', {
          query,
          type: organizationType,
          country,
          region,
          city,
          zipCode,
          limit,
        });
      }

      result = organizations;
    } catch (error) {
      console.error(error);
    } finally {
      setHasSearched(true);
    }

    return result;
  };

  const handleCannotFindOrg = () => {
    onSetNeedsCustomOrganizationName(true);
  };

  const handleAdjustLocation = () => {
    handleClearLocation();
  };

  const handleAddMoreDetails = () => {
    onSetNeedsCustomOrganizationName(false);
    orgTARef.current?.input?.focus();
    orgTARef.current?.input?.select();
  };

  const handleRetrySearch = () => {
    handleClearOrg();
  };

  // Handle custom organization submission
  const handleCustomOrgSelect = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newCustomOrgName = event.target.value;
    onSetCustomOrganizationName(newCustomOrgName);

    if (selectedLocation?.place && organizationType) {
      const { city, region, country } = selectedLocation.place;

      onSelectOrganization(
        newCustomOrgName
          ? {
              organization: {
                id: '00000000-0000-0000-0000-000000000000',
                name: newCustomOrgName.trim(),
                type: organizationType,
                city: city,
                region: region,
                country: country,
                custom: true,
              },
            }
          : null,
      );
    }
  };

  const handleClearLocation = () => {
    // When we clear the city, also need to clear the org
    // Otherwise could be disabled when go back to it
    handleClearOrg();
    onSetNeedsCustomOrganizationName(false);
    onSelectLocation(null);
    locationTARef.current?.dropdown?.selectItem(null);

    const inputValue = locationTARef.current?.input?.input?.value;
    if (inputValue) {
      locationTARef.current?.dropdown?.setInputValue(inputValue);
    }

    // setTimeout hack to push this to next event loop iteration
    setTimeout(() => {
      locationTARef.current?.input?.focus();
      locationTARef.current?.input?.select();
    });
  };

  const handleClearOrg = () => {
    // If they were entering a custom org, we'll prefill the org search with that text
    // otherwise we'll use the previous contents
    const inputValue = needsCustomOrganizationName
      ? customOrganizationName
      : orgTARef.current?.input?.input?.value;
    onSelectOrganization(null);
    onSetNeedsCustomOrganizationName(false);
    onSetCustomOrganizationName(undefined);

    orgTARef.current?.dropdown?.selectItem(null);

    if (inputValue) {
      orgTARef.current?.dropdown?.setInputValue(inputValue);
    }

    // setTimeout hack to push this to next event loop iteration
    setTimeout(() => {
      orgTARef.current?.input?.focus();
      orgTARef.current?.input?.select();
    });
  };

  const handleLocationFetchError = () => {
    onLocationFetchError(
      t(
        'Oops, we are having an issue searching for your city Please proceed entering your school manually',
        'Oops, we are having an issue searching for your city. Please proceed entering your school manually.',
      ),
    );
    onSetNeedsCustomOrganizationName(true);

    // This is an edge case, but it might be good to add code to focus on that input
  };

  // Create dynamic label for city input
  const getCityLabel = () => {
    if (!organizationType)
      return t('City (if unsure, try approximate)', 'City (if unsure, try approximate)');
    console.log('organizationType', organizationType);
    return t(
      "Your {organizationType}'s City (if unsure, try approximate)",
      "Your {organizationType}'s City (if unsure, try approximate)",
      { organizationType: t(organizationType, organizationType).toLocaleLowerCase() },
    );
  };

  // Create dynamic label for organization input
  const getOrgLabel = () => {
    if (!organizationType) return t('Organization Name', 'Organization Name');
    return t(`{organizationType} Name`, `{organizationType} Name`, {
      organizationType: t(organizationType, organizationType),
    });
  };

  const tokenizeQuery = (query: string) => {
    return (
      query
        ?.split(' ')
        .map((term: string) => term.trim())
        .filter((term: string) => term) || []
    );
  };

  const highlightText = (text: string, tokens: string[]) => {
    if (!tokens.length) return text;

    // Escape special characters for regex
    const regex = new RegExp(
      tokens.map(token => token.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')).join('|'),
      'gi',
    );

    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) => {
          const match = text.match(regex)?.[index];
          return (
            <React.Fragment key={index}>
              {part}
              {match && (
                <span
                  style={{
                    backgroundColor: '#e6d5c0',
                    fontWeight: 'bold',
                  }}
                >
                  {match}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const formatOrgToString = (
    org: SelectedOrganizationType['organizationAutocomplete'] | null,
  ) => {
    if (!org) {
      return '';
    }
    const { name, city, region } = org;
    return `${name}, ${city}, ${region}`;
  };

  const isInternationalOrIncomplete = () =>
    selectedLocation?.place?.country !== 'US' && !locationFetchError;

  return (
    <Container className={className} style={style} $fluid={fluid}>
      {!hideOrganizationTypePicker && (
        <Section className="section">
          <Select
            label={t('Organization Level', 'Organization Level')}
            labelStyle={{ fontSize: 14, color: theme.colors.black, fontWeight: 'bold' }}
            options={orgTypeOptions}
            value={organizationType as string | undefined}
            onSelect={value => {
              logEvent('OrganizationLevel.Changed', {
                choice: value,
              });
              onSelectOrganizationType(value as OrganizationType);
              onSelectLocation(null);

              locationTARef.current?.dropdown?.setInputValue('');
              locationTARef.current?.input?.input?.focus();
            }}
            fluid={fluid}
            placeholder={t('Select organization level', 'Select organization level')}
          />
        </Section>
      )}

      {organizationType && (
        <Section className="section">
          <TypeAheadInput<GooglePlaceAutocomplete>
            ref={locationTARef}
            fetchSuggestions={fetchCities}
            itemToString={item => (item ? item.description : '')}
            initialInputValue={selectedLocation?.placeAutocomplete.description}
            initialSelectedItem={selectedLocation?.placeAutocomplete}
            onFetchError={handleLocationFetchError}
            error={locationFetchError}
            disabled={!!locationFetchError}
            onSelect={async item => {
              logEvent('Location.Changed', {
                choice: item,
              });

              if (!item) {
                onSelectLocation(null);
                return;
              }

              try {
                const details = await fetchDetails(item.place_id);
                onSelectLocation({
                  place: details,
                  placeAutocomplete: item,
                });
              } catch (error) {
                handleLocationFetchError();
              }
            }}
            placeholder={t('Search for a city', 'Search for a city...')}
            label={getCityLabel()}
            showClearButton={!!selectedLocation}
            onClear={handleClearLocation}
            fluid={fluid}
            minChars={1}
            debounceMs={500}
            labelStyle={{ fontSize: 14, color: theme.colors.black, fontWeight: 'bold' }}
            showResultsHeader={true}
            maxResults={10}
            showNoResultsMessage={true}
            footerComponent={
              <AutocompleteFooter>
                <span>
                  {t(
                    'Not seeing your city? Try searching for a different nearby city If your {organizationType} is in a neighborhood of a larger city, try searching for the larger city',
                    'Not seeing your city? Try searching for a different nearby city. If your {organizationType} is in a neighborhood of a larger city, try searching for the larger city.',
                    {
                      organizationType: t(
                        organizationType,
                        organizationType,
                      ).toLowerCase(),
                    },
                  )}
                </span>
              </AutocompleteFooter>
            }
          />
        </Section>
      )}
      {organizationType &&
        (selectedLocation || locationFetchError) &&
        (needsCustomOrganizationName || isInternationalOrIncomplete()) && (
          <Section className="section">
            <Input
              label={`Enter ${getOrgLabel()}`}
              labelStyle={{ fontSize: 14, color: theme.colors.black, fontWeight: 'bold' }}
              className="customInput"
              value={customOrganizationName}
              onChange={event => {
                handleCustomOrgSelect(event);
              }}
            />
            <span style={{ fontWeight: 'normal' }}>
              {!isInternationalOrIncomplete() && customOrganizationName ? (
                <>
                  <p>
                    <T
                      keyName="You will be added to {organizationType}: <b>{customOrganizationName}</b>"
                      defaultValue="You will be added to {organizationType}: <b>{customOrganizationName}</b>"
                      params={{
                        b: <b />,
                        organizationType: t(
                          organizationType,
                          organizationType,
                        ).toLocaleLowerCase(),
                        customOrganizationName,
                      }}
                    />
                  </p>
                  <p>
                    {t(
                      "Try to be as accurate as possible in order for us to get you to {organizationType}'s benefits and privileges!",
                      "Try to be as accurate as possible in order for us to get you to {organizationType}'s benefits and privileges!",
                      {
                        organizationType: t(
                          organizationType,
                          organizationType,
                        ).toLocaleLowerCase(),
                      },
                    )}
                  </p>
                </>
              ) : null}
              {!isInternationalOrIncomplete() && !customOrganizationName ? (
                <>
                  <p>
                    {t(
                      "Try to be as accurate as possible in order for us to get you to {organizationType}'s benefits and privileges!",
                      "Try to be as accurate as possible in order for us to get you to {organizationType}'s benefits and privileges!",
                      {
                        organizationType: t(
                          organizationType,
                          organizationType,
                        ).toLocaleLowerCase(),
                      },
                    )}
                  </p>
                </>
              ) : null}
              {!isInternationalOrIncomplete() &&
              !disableSearchRetry &&
              !locationFetchError ? (
                <>
                  <b>
                    <a
                      onClick={() => {
                        logEvent('OrganizationSearchRetry.Clicked');
                        handleRetrySearch();
                      }}
                      style={{ color: theme.colors.blue }}
                    >
                      {t('Retry your search', 'Retry your search')}
                    </a>
                  </b>{' '}
                  {t(
                    "if you'd like to try finding your {organizationType} again",
                    "if you'd like to try finding your {organizationType} again.",
                    {
                      organizationType: t(
                        organizationType,
                        organizationType,
                      ).toLocaleLowerCase(),
                    },
                  )}
                </>
              ) : null}
            </span>
          </Section>
        )}
      {organizationType &&
        selectedLocation &&
        !needsCustomOrganizationName &&
        !isInternationalOrIncomplete() && (
          <Section className="section">
            <TypeAheadInput<OrganizationAutocomplete>
              ref={orgTARef}
              fetchSuggestions={fetchOrganizations}
              itemToString={formatOrgToString}
              initialInputValue={formatOrgToString(
                selectedOrganization?.organizationAutocomplete,
              )}
              initialSelectedItem={selectedOrganization?.organizationAutocomplete}
              MenuItemChildren={({
                item,
                inputValue: query,
              }: {
                item: OrganizationAutocomplete;
                inputValue: string;
              }) => (
                <span
                  className="item-name"
                  style={{
                    fontWeight: 'normal',
                  }}
                >
                  {highlightText(item.name, tokenizeQuery(query))}{' '}
                  <i
                    className="item-location"
                    style={{
                      fontStyle: 'italic',
                      color: theme.colors.greyDark,
                    }}
                  >
                    {item.city}, {item.region}
                  </i>
                </span>
              )}
              onSelect={async item => {
                logEvent('Organization.Changed', {
                  choice: item,
                });

                if (!item) {
                  onSelectOrganization(null);
                  return;
                }

                onSelectOrganization({
                  organization: {
                    ...item,
                    custom: false,
                    type: organizationType,
                  },
                  organizationAutocomplete: item,
                });
              }}
              placeholder={t(
                'Search for a {organizationType}',
                'Search for a {organizationType}...',
                {
                  organizationType: t(organizationType, organizationType).toLowerCase(),
                },
              )}
              showClearButton={!!selectedOrganization}
              onClear={() => {
                logEvent('OrganizationClear.Clicked');
                handleClearOrg();
              }}
              label={getOrgLabel()}
              labelStyle={{ fontSize: 14, color: theme.colors.black, fontWeight: 'bold' }}
              fluid={fluid}
              minChars={1}
              debounceMs={500}
              showResultsHeader={true}
              maxResults={50}
              showNoResultsMessage={true}
              footerComponent={
                <AutocompleteFooter>
                  <span>
                    <T
                      keyName="Not seeing your {organizationType}?"
                      defaultValue="Not seeing your {organizationType}?"
                      params={{
                        organizationType: t(
                          organizationType,
                          organizationType,
                        ).toLowerCase(),
                      }}
                    />{' '}
                    <a
                      onClick={() => {
                        logEvent('OrganizationAddDetails.Clicked');
                        handleAddMoreDetails();
                      }}
                    >
                      <T
                        keyName="Add a more detailed {organizationType} name"
                        defaultValue="Add a more detailed {organizationType} name"
                        params={{
                          organizationType: t(
                            organizationType,
                            organizationType,
                          ).toLowerCase(),
                        }}
                      />
                    </a>{' '}
                    or{' '}
                    <a
                      onClick={() => {
                        logEvent('OrganizationAdjustLocation.Clicked');
                        handleAdjustLocation();
                      }}
                    >
                      <T
                        keyName="try adjusting your city"
                        defaultValue="try adjusting your city"
                      />
                    </a>
                    .{' '}
                    <T
                      keyName="If you're still unable to find your school"
                      defaultValue="If you're still unable to find your school"
                    />{' '}
                    <a
                      onClick={() => {
                        logEvent('OrganizationAdjustLocation.Clicked');
                        handleCannotFindOrg();
                      }}
                    >
                      <T keyName="click here" defaultValue="click here" />
                    </a>
                    .
                  </span>
                </AutocompleteFooter>
              }
            />
            {hasSearched && !selectedOrganization && (
              <StillCannotFindOrg>
                <p>
                  <T
                    keyName="If you're still unable to find your school"
                    defaultValue="If you're still unable to find your school"
                  />{' '}
                  <a
                    onClick={() => {
                      logEvent('OrganizationAdjustLocation.Clicked');
                      handleCannotFindOrg();
                    }}
                  >
                    <T keyName="click here" defaultValue="click here" />
                  </a>
                  .
                </p>
              </StillCannotFindOrg>
            )}
          </Section>
        )}
    </Container>
  );
}

const Container = styled.div<{ $fluid?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ $fluid }) => ($fluid ? '100%' : 'auto')};
  gap: 10px;
  min-height: 150px;
  max-width: 400px;

  /* Ensure consistent spacing between sections */
  & > div.section {
    margin-bottom: 10px;
  }

  /* Remove margin from the last section */
  & > div.section:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 700px) {
    gap: 15px;

    & > div.section {
      margin-bottom: 15px;
    }
  }
`;

const StillCannotFindOrg = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  a {
    color: ${theme.colors.blue};
    cursor: pointer;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 0;

  input:disabled {
    color: ${theme.colors.black};
    cursor: default;
  }

  /* Override TypeAheadInput's margin to ensure consistent spacing */
  & > div {
    margin-bottom: 0;
  }
`;

const AutocompleteFooter = styled.div`
  margin: -8px -12px;
  padding: 8px 12px;

  font-size: 12px;

  a {
    color: ${theme.colors.blue};
  }
`;
